import React, { createContext, useContext, useReducer, useEffect, useLayoutEffect, ReactNode } from 'react';
import { useSettings } from './SettingsContext';

// Types
export interface ThemeColors {
  primary: string;
  secondary: string;
  accent: string;
  background: string;
  surface: string;
  text: string;
  textSecondary: string;
  border: string;
  success: string;
  warning: string;
  error: string;
  info: string;
}

export interface ThemeConfig {
  colors: ThemeColors;
  fontFamily: string;
  borderRadius: string;
  boxShadow: string;
  fontSize: {
    xs: string;
    sm: string;
    base: string;
    lg: string;
    xl: string;
    '2xl': string;
    '3xl': string;
  };
  spacing: {
    xs: string;
    sm: string;
    md: string;
    lg: string;
    xl: string;
    '2xl': string;
  };
  animation: {
    duration: string;
    easing: string;
  };
}

export interface ThemeState {
  config: ThemeConfig;
  isDark: boolean;
  isCustomized: boolean;
}

// Default themes
const defaultLightTheme: ThemeConfig = {
  colors: {
    primary: '#2563EB',
    secondary: '#1E40AF',
    accent: '#3B82F6',
    background: '#F1F5F9',
    surface: '#FFFFFF',
    text: '#0F172A',
    textSecondary: '#64748B',
    border: '#E2E8F0',
    success: '#10B981',
    warning: '#F59E0B',
    error: '#EF4444',
    info: '#2563EB',
  },
  fontFamily: 'Times New Roman, serif',
  borderRadius: '8px',
  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.07), 0 2px 4px -2px rgba(0, 0, 0, 0.05)',
  fontSize: {
    xs: '0.75rem',
    sm: '0.875rem',
    base: '1rem',
    lg: '1.125rem',
    xl: '1.25rem',
    '2xl': '1.5rem',
    '3xl': '1.875rem',
  },
  spacing: {
    xs: '0.25rem',
    sm: '0.5rem',
    md: '1rem',
    lg: '1.5rem',
    xl: '2rem',
    '2xl': '3rem',
  },
  animation: {
    duration: '0.3s',
    easing: 'cubic-bezier(0.4, 0, 0.2, 1)',
  },
};

const defaultDarkTheme: ThemeConfig = {
  ...defaultLightTheme,
  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.3), 0 2px 4px -2px rgba(0, 0, 0, 0.2)',
  colors: {
    primary: '#3B82F6',
    secondary: '#60A5FA',
    accent: '#93C5FD',
    background: '#0F172A',
    surface: '#1E293B',
    text: '#F8FAFC',
    textSecondary: '#94A3B8',
    border: '#334155',
    success: '#34D399',
    warning: '#FBBF24',
    error: '#F87171',
    info: '#60A5FA',
  },
};

// Action types
type ThemeAction =
  | { type: 'SET_THEME'; payload: Partial<ThemeConfig> }
  | { type: 'TOGGLE_DARK_MODE' }
  | { type: 'RESET_THEME' }
  | { type: 'SET_CUSTOMIZED'; payload: boolean }
  | { type: 'LOAD_THEME'; payload: ThemeState };

// Initial state
const initialState: ThemeState = {
  config: defaultLightTheme,
  isDark: false,
  isCustomized: false,
};

// Reducer
const themeReducer = (state: ThemeState, action: ThemeAction): ThemeState => {
  switch (action.type) {
    case 'SET_THEME':
      return {
        ...state,
        config: { ...state.config, ...action.payload },
        isCustomized: true,
      };
    case 'TOGGLE_DARK_MODE': {
      const newIsDark = !state.isDark;
      const newColors = newIsDark ? defaultDarkTheme.colors : defaultLightTheme.colors;
      return {
        ...state,
        isDark: newIsDark,
        config: {
          ...state.config,
          colors: {
            ...state.config.colors,
            ...newColors
          }
        }
      };
    }
    case 'RESET_THEME':
      return {
        config: defaultLightTheme,
        isDark: false,
        isCustomized: false,
      };
    case 'SET_CUSTOMIZED':
      return {
        ...state,
        isCustomized: action.payload,
      };
    case 'LOAD_THEME': {
      const loaded = action.payload;
      const palette = loaded.isDark ? defaultDarkTheme.colors : defaultLightTheme.colors;
      return {
        ...loaded,
        config: {
          ...loaded.config,
          colors: {
            ...loaded.config.colors,
            ...palette
          }
        }
      };
    }
    default:
      return state;
  }
};

// Context
interface ThemeContextType {
  theme: ThemeConfig;
  isDark: boolean;
  isCustomized: boolean;
  activeColor: string;
  setTheme: (config: Partial<ThemeConfig>) => void;
  toggleDarkMode: () => void;
  resetTheme: () => void;
  setCustomized: (customized: boolean) => void;
  getCSSVariables: () => Record<string, string>;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

// Provider component
interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  // Read isDark from localStorage on mount
  const getInitialIsDark = () => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem('isDark');
      if (stored === 'true') return true;
      if (stored === 'false') return false;
    }
    return false;
  };

  const [isDark, setIsDark] = React.useState(getInitialIsDark);
  const [hydrated, setHydrated] = React.useState(false);
  const { settings } = useSettings();

  // Listen for settings.theme changes and update theme colors
  useEffect(() => {
    const themeSettings = (settings as any)?.theme;
    if (themeSettings) {
      const { primaryColor, secondaryColor } = themeSettings;
      if (primaryColor || secondaryColor) {
        const root = document.documentElement;
        if (primaryColor) root.style.setProperty('--color-primary', primaryColor);
        if (secondaryColor) root.style.setProperty('--color-secondary', secondaryColor);
      }
    }
  }, [settings]);

  // Set theme config based on isDark
  const themeConfig = React.useMemo(() => {
    return isDark ? defaultDarkTheme : defaultLightTheme;
  }, [isDark]);

  // Save isDark to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('isDark', isDark ? 'true' : 'false');
  }, [isDark]);

  // Hydration guard
  useEffect(() => {
    setHydrated(true);
  }, []);

  // Apply theme to document
  useEffect(() => {
    const root = document.documentElement;
    const cssVariables = getCSSVariables();
    Object.entries(cssVariables).forEach(([key, value]) => {
      root.style.setProperty(key, value);
    });
    document.body.style.fontFamily = themeConfig.fontFamily;
    if (isDark) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [themeConfig, isDark]);

  // Apply dark mode class before first paint
  useLayoutEffect(() => {
    const stored = localStorage.getItem('isDark');
    if (stored === 'true') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, []);

  // Toggle dark mode function
  const toggleDarkMode = () => {
    setIsDark((prev) => !prev);
  };

  // Get CSS variables function
  const getCSSVariables = (): Record<string, string> => {
    const { colors, fontSize, spacing, animation } = themeConfig;
    return {
      '--color-primary': colors.primary,
      '--color-primary-dark': isDark ? '#1D4ED8' : '#1E40AF',
      '--color-primary-light': isDark ? 'rgba(59, 130, 246, 0.2)' : 'rgba(37, 99, 235, 0.12)',
      '--color-secondary': colors.secondary,
      '--color-secondary-dark': isDark ? '#2563EB' : '#172554',
      '--color-secondary-light': isDark ? 'rgba(96, 165, 250, 0.2)' : 'rgba(30, 64, 175, 0.1)',
      '--color-accent': colors.accent,
      '--color-background': colors.background,
      '--color-background-hover': isDark ? '#1E293B' : '#E2E8F0',
      '--color-surface': colors.surface,
      '--color-surface-light': isDark ? '#334155' : '#F8FAFC',
      '--color-surface-hover': isDark ? '#334155' : '#F1F5F9',
      '--color-text': colors.text,
      '--color-text-secondary': colors.textSecondary,
      '--color-border': colors.border,
      '--color-success': colors.success,
      '--color-success-light': isDark ? 'rgba(52, 211, 153, 0.2)' : 'rgba(16, 185, 129, 0.12)',
      '--color-warning': colors.warning,
      '--color-warning-light': isDark ? 'rgba(251, 191, 36, 0.2)' : 'rgba(245, 158, 11, 0.12)',
      '--color-error': colors.error,
      '--color-error-light': isDark ? 'rgba(248, 113, 113, 0.2)' : 'rgba(239, 68, 68, 0.12)',
      '--color-danger': colors.error,
      '--color-danger-light': isDark ? 'rgba(248, 113, 113, 0.2)' : 'rgba(239, 68, 68, 0.12)',
      '--color-info': colors.info,
      '--color-on-primary': '#FFFFFF',
      '--color-on-secondary': '#FFFFFF',
      '--color-on-surface': colors.text,
      '--color-on-background': colors.text,
      '--color-on-success': '#FFFFFF',
      '--color-on-warning': '#000000',
      '--color-on-error': '#FFFFFF',
      '--color-on-info': '#FFFFFF',
      '--sidebar-hover-bg': isDark ? '#334155' : '#F1F5F9',
      '--color-red-100': isDark ? 'rgba(239, 68, 68, 0.2)' : '#FEE2E2',
      '--color-red-600': isDark ? '#F87171' : '#DC2626',
      '--color-yellow-100': isDark ? 'rgba(245, 158, 11, 0.2)' : '#FEF3C7',
      '--color-yellow-600': isDark ? '#FBBF24' : '#D97706',
      '--color-green-100': isDark ? 'rgba(16, 185, 129, 0.2)' : '#D1FAE5',
      '--color-green-600': isDark ? '#34D399' : '#059669',
      '--font-size-xs': fontSize.xs,
      '--font-size-sm': fontSize.sm,
      '--font-size-base': fontSize.base,
      '--font-size-lg': fontSize.lg,
      '--font-size-xl': fontSize.xl,
      '--font-size-2xl': fontSize['2xl'],
      '--font-size-3xl': fontSize['3xl'],
      '--spacing-xs': spacing.xs,
      '--spacing-sm': spacing.sm,
      '--spacing-md': spacing.md,
      '--spacing-lg': spacing.lg,
      '--spacing-xl': spacing.xl,
      '--spacing-2xl': spacing['2xl'],
      '--border-radius': themeConfig.borderRadius,
      '--box-shadow': themeConfig.boxShadow,
      '--animation-duration': animation.duration,
      '--animation-easing': animation.easing,
    };
  };

  const value: ThemeContextType = {
    theme: themeConfig,
    isDark,
    isCustomized: false,
    activeColor: '', // Placeholder, will be updated by SettingsContext
    setTheme() {/* TODO: implement setTheme or leave as no-op */},
    toggleDarkMode,
    resetTheme() {/* TODO: implement resetTheme or leave as no-op */},
    setCustomized() {/* TODO: implement setCustomized or leave as no-op */},
    getCSSVariables,
  };

  if (!hydrated) return null;

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

// Hook to use theme context
export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

// Utility functions
export const createTheme = (overrides: Partial<ThemeConfig>): ThemeConfig => {
  return {
    ...defaultLightTheme,
    ...overrides,
    colors: {
      ...defaultLightTheme.colors,
      ...overrides.colors,
    },
    fontSize: {
      ...defaultLightTheme.fontSize,
      ...overrides.fontSize,
    },
    spacing: {
      ...defaultLightTheme.spacing,
      ...overrides.spacing,
    },
    animation: {
      ...defaultLightTheme.animation,
      ...overrides.animation,
    },
  };
};

export const getContrastColor = (backgroundColor: string): string => {
  // Simple contrast calculation
  const hex = backgroundColor.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;
  return brightness > 128 ? '#000000' : '#FFFFFF';
};

export const adjustColor = (color: string, amount: number): string => {
  const hex = color.replace('#', '');
  const r = Math.max(0, Math.min(255, parseInt(hex.substr(0, 2), 16) + amount));
  const g = Math.max(0, Math.min(255, parseInt(hex.substr(2, 2), 16) + amount));
  const b = Math.max(0, Math.min(255, parseInt(hex.substr(4, 2), 16) + amount));
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}; 